import React, { useState, useEffect, useRef } from 'react';
import axios from '@/utils/axiosConfig';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TrendingUp, CheckCircle, XCircle, AlertCircle, Plus, Target, Star, BarChart3, Clock, User, Award, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const Performances = ({ user, token, API }) => {
  const [activeSection, setActiveSection] = useState('dashboard'); // dashboard, tasks, reviews, objectives
  const [tasks, setTasks] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Task Form
  const [taskForm, setTaskForm] = useState({
    employee_id: '',
    title: '',
    description: '',
    priority: 'medium',
    deadline: ''
  });
  
  // Review Form
  const [reviewForm, setReviewForm] = useState({
    employee_id: '',
    review_period: '',
    overall_rating: 3,
    strengths: '',
    areas_for_improvement: '',
    goals_achieved: '',
    new_goals: '',
    comments: ''
  });
  
  // Objective Form
  const [objectiveForm, setObjectiveForm] = useState({
    employee_id: '',
    title: '',
    description: '',
    target_value: 100,
    unit: '%',
    deadline: ''
  });

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    fetchData();
  }, [activeSection]);

  const fetchData = async () => {
    try {
      if (isMountedRef.current) setLoading(true);
      
      // Always fetch stats for dashboard
      if (activeSection === 'dashboard' && (user.role === 'admin' || user.role === 'sub_admin')) {
        try {
          const statsResponse = await axios.get(`${API}/performance-stats`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (isMountedRef.current) setStats(statsResponse.data);
        } catch (err) {
          console.error('Failed to fetch stats:', err);
        }
      }
      
      if (activeSection === 'tasks' || activeSection === 'dashboard') {
        const response = await axios.get(`${API}/tasks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTasks(response.data);
      }
      
      if (activeSection === 'reviews' || activeSection === 'dashboard') {
        const response = await axios.get(`${API}/performance-reviews`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReviews(response.data);
      }
      
      if (activeSection === 'objectives' || activeSection === 'dashboard') {
        const response = await axios.get(`${API}/objectives`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setObjectives(response.data);
      }
      
      // Fetch employees if admin
      if (user.role === 'admin' || user.role === 'sub_admin') {
        const usersResponse = await axios.get(`${API}/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEmployees(usersResponse.data.filter(u => u.role === 'employee'));
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Erreur lors du chargement des données');
      setLoading(false);
    }
  };

  const handleCreateTask = async () => {
    if (!taskForm.title || !taskForm.description || !taskForm.employee_id) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await axios.post(`${API}/tasks`, taskForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Tâche créée avec succès');
      setDialogOpen(false);
      setTaskForm({ employee_id: '', title: '', description: '', priority: 'medium', deadline: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleCreateReview = async () => {
    if (!reviewForm.employee_id || !reviewForm.review_period) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await axios.post(`${API}/performance-reviews`, reviewForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Évaluation créée avec succès');
      setDialogOpen(false);
      setReviewForm({
        employee_id: '',
        review_period: '',
        overall_rating: 3,
        strengths: '',
        areas_for_improvement: '',
        goals_achieved: '',
        new_goals: '',
        comments: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleCreateObjective = async () => {
    if (!objectiveForm.employee_id || !objectiveForm.title) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await axios.post(`${API}/objectives`, objectiveForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Objectif créé avec succès');
      setDialogOpen(false);
      setObjectiveForm({ employee_id: '', title: '', description: '', target_value: 100, unit: '%', deadline: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleUpdateObjective = async (objectiveId, currentValue) => {
    try {
      await axios.put(`${API}/objectives/${objectiveId}`, { current_value: parseFloat(currentValue) }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Objectif mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { label: 'Faible', color: 'bg-gray-100 text-gray-800' },
      medium: { label: 'Moyen', color: 'bg-blue-100 text-blue-800' },
      high: { label: 'Élevé', color: 'bg-orange-100 text-orange-800' },
      urgent: { label: 'Urgent', color: 'bg-red-100 text-red-800' }
    };
    return config[priority] || config.medium;
  };

  const getStatusBadge = (status) => {
    const config = {
      en_cours: { label: 'En Cours', color: 'bg-blue-100 text-blue-800', icon: Clock },
      termine: { label: 'Terminé', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      annule: { label: 'Annulé', color: 'bg-red-100 text-red-800', icon: XCircle },
      atteint: { label: 'Atteint', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      non_atteint: { label: 'Non Atteint', color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const cfg = config[status] || config.en_cours;
    const Icon = cfg.icon;
    return (
      <Badge className={`${cfg.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {cfg.label}
      </Badge>
    );
  };

  // Calculate local stats for dashboard
  const localStats = {
    totalTasks: tasks.length,
    completedTasks: tasks.filter(t => t.status === 'termine').length,
    inProgressTasks: tasks.filter(t => t.status === 'en_cours').length,
    totalObjectives: objectives.length,
    achievedObjectives: objectives.filter(o => o.status === 'atteint').length,
    avgRating: reviews.length > 0 
      ? (reviews.reduce((sum, r) => sum + (r.overall_rating || 0), 0) / reviews.length).toFixed(1)
      : 0
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-gray-200 overflow-x-auto">
        {[
          { id: 'dashboard', name: '📊 Tableau de Bord', count: null, adminOnly: true },
          { id: 'tasks', name: '📋 Tâches', count: tasks.length },
          { id: 'reviews', name: '⭐ Évaluations', count: reviews.length },
          { id: 'objectives', name: '🎯 Objectifs', count: objectives.length }
        ].filter(s => !s.adminOnly || (user.role === 'admin' || user.role === 'sub_admin')).map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`px-4 py-2 font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeSection === section.id
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {section.name} {section.count !== null && `(${section.count})`}
          </button>
        ))}
      </div>

      {/* Dashboard Section */}
      {activeSection === 'dashboard' && (user.role === 'admin' || user.role === 'sub_admin') && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-0 shadow-xl bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <CheckCircle className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Tâches Terminées</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stats?.summary?.completed_tasks || localStats.completedTasks} / {stats?.summary?.total_tasks || localStats.totalTasks}
                    </p>
                    <p className="text-sm text-blue-600">
                      {stats?.summary?.task_completion_rate || (localStats.totalTasks > 0 ? ((localStats.completedTasks / localStats.totalTasks) * 100).toFixed(0) : 0)}% complété
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl bg-gradient-to-br from-green-50 to-emerald-50">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <Target className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Objectifs Atteints</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stats?.summary?.achieved_objectives || localStats.achievedObjectives} / {stats?.summary?.total_objectives || localStats.totalObjectives}
                    </p>
                    <p className="text-sm text-green-600">
                      {stats?.summary?.objective_achievement_rate || (localStats.totalObjectives > 0 ? ((localStats.achievedObjectives / localStats.totalObjectives) * 100).toFixed(0) : 0)}% atteint
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl bg-gradient-to-br from-yellow-50 to-amber-50">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-yellow-100 rounded-xl">
                    <Star className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Note Moyenne</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stats?.summary?.avg_rating || localStats.avgRating} / 5
                    </p>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map(i => (
                        <Star
                          key={i}
                          className={`h-4 w-4 ${i <= (stats?.summary?.avg_rating || localStats.avgRating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-xl bg-gradient-to-br from-purple-50 to-pink-50">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-xl">
                    <Award className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Évaluations</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stats?.summary?.total_reviews || reviews.length}
                    </p>
                    <p className="text-sm text-purple-600">
                      {employees.length} employé(s)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Employee Performance Table */}
          {stats?.employee_stats && stats.employee_stats.length > 0 && (
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-purple-600" />
                  Performance par Employé
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Employé</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Tâches</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Taux Complétion</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Objectifs</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Note Moy.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {stats.employee_stats.map((emp) => (
                        <tr key={emp.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-gray-400" />
                              <span className="font-medium">{emp.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-green-600 font-semibold">{emp.completed_tasks}</span>
                            <span className="text-gray-500"> / {emp.total_tasks}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-24 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-gradient-to-r from-purple-600 to-pink-600 h-2 rounded-full"
                                  style={{ width: `${emp.task_completion_rate}%` }}
                                />
                              </div>
                              <span className="text-sm font-medium">{emp.task_completion_rate}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="text-green-600 font-semibold">{emp.achieved_objectives}</span>
                            <span className="text-gray-500"> / {emp.total_objectives}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Star className={`h-4 w-4 ${emp.avg_rating >= 1 ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                              <span className="font-medium">{emp.avg_rating || '-'}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              onClick={() => setActiveSection('tasks')}
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-blue-50"
            >
              <Zap className="h-6 w-6 text-blue-600" />
              <span>Gérer les Tâches</span>
            </Button>
            <Button
              onClick={() => setActiveSection('reviews')}
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-yellow-50"
            >
              <Star className="h-6 w-6 text-yellow-600" />
              <span>Créer une Évaluation</span>
            </Button>
            <Button
              onClick={() => setActiveSection('objectives')}
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 hover:bg-green-50"
            >
              <Target className="h-6 w-6 text-green-600" />
              <span>Définir des Objectifs</span>
            </Button>
          </div>
        </div>
      )}

      {/* Actions */}
      {activeSection !== 'dashboard' && (
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">
          {activeSection === 'tasks' && '📋 Tâches Assignées'}
          {activeSection === 'reviews' && '⭐ Évaluations de Performance'}
          {activeSection === 'objectives' && '🎯 Objectifs & KPI'}
        </h2>

        {/* Employees can create tasks, but only admin/sub_admin can create reviews/objectives */}
        {((user.role === 'admin' || user.role === 'sub_admin') || (user.role === 'employee' && activeSection === 'tasks')) && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-purple-600 to-pink-600">
                <Plus className="mr-2 h-4 w-4" />
                {activeSection === 'tasks' && 'Nouvelle Tâche'}
                {activeSection === 'reviews' && 'Nouvelle Évaluation'}
                {activeSection === 'objectives' && 'Nouvel Objectif'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {activeSection === 'tasks' && 'Créer une Tâche'}
                  {activeSection === 'reviews' && 'Créer une Évaluation'}
                  {activeSection === 'objectives' && 'Créer un Objectif'}
                </DialogTitle>
              </DialogHeader>

              {activeSection === 'tasks' && (
                <div className="space-y-4">
                  <div>
                    <Label>Employé *</Label>
                    <Select value={taskForm.employee_id} onValueChange={(val) => setTaskForm({...taskForm, employee_id: val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un employé" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Titre *</Label>
                    <Input value={taskForm.title} onChange={(e) => setTaskForm({...taskForm, title: e.target.value})} />
                  </div>
                  <div>
                    <Label>Description *</Label>
                    <Textarea value={taskForm.description} onChange={(e) => setTaskForm({...taskForm, description: e.target.value})} rows={4} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Priorité</Label>
                      <Select value={taskForm.priority} onValueChange={(val) => setTaskForm({...taskForm, priority: val})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Faible</SelectItem>
                          <SelectItem value="medium">Moyen</SelectItem>
                          <SelectItem value="high">Élevé</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Date Limite</Label>
                      <Input type="date" value={taskForm.deadline} onChange={(e) => setTaskForm({...taskForm, deadline: e.target.value})} />
                    </div>
                  </div>
                  <Button onClick={handleCreateTask} className="w-full bg-gradient-to-r from-purple-600 to-pink-600">
                    Créer la Tâche
                  </Button>
                </div>
              )}

              {activeSection === 'reviews' && (
                <div className="space-y-4">
                  <div>
                    <Label>Employé *</Label>
                    <Select value={reviewForm.employee_id} onValueChange={(val) => setReviewForm({...reviewForm, employee_id: val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un employé" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Période d'Évaluation *</Label>
                    <Input placeholder="Ex: Q1 2025, 2025" value={reviewForm.review_period} onChange={(e) => setReviewForm({...reviewForm, review_period: e.target.value})} />
                  </div>
                  <div>
                    <Label>Note Globale (0-5) *</Label>
                    <div className="flex items-center gap-2">
                      <Input type="number" min="0" max="5" step="0.5" value={reviewForm.overall_rating} onChange={(e) => setReviewForm({...reviewForm, overall_rating: parseFloat(e.target.value)})} />
                      <span className="text-sm text-gray-500">/5</span>
                    </div>
                  </div>
                  <div>
                    <Label>Points Forts</Label>
                    <Textarea value={reviewForm.strengths} onChange={(e) => setReviewForm({...reviewForm, strengths: e.target.value})} rows={2} />
                  </div>
                  <div>
                    <Label>Axes d'Amélioration</Label>
                    <Textarea value={reviewForm.areas_for_improvement} onChange={(e) => setReviewForm({...reviewForm, areas_for_improvement: e.target.value})} rows={2} />
                  </div>
                  <div>
                    <Label>Objectifs Atteints</Label>
                    <Textarea value={reviewForm.goals_achieved} onChange={(e) => setReviewForm({...reviewForm, goals_achieved: e.target.value})} rows={2} />
                  </div>
                  <div>
                    <Label>Nouveaux Objectifs</Label>
                    <Textarea value={reviewForm.new_goals} onChange={(e) => setReviewForm({...reviewForm, new_goals: e.target.value})} rows={2} />
                  </div>
                  <div>
                    <Label>Commentaires</Label>
                    <Textarea value={reviewForm.comments} onChange={(e) => setReviewForm({...reviewForm, comments: e.target.value})} rows={2} />
                  </div>
                  <Button onClick={handleCreateReview} className="w-full bg-gradient-to-r from-purple-600 to-pink-600">
                    Créer l'Évaluation
                  </Button>
                </div>
              )}

              {activeSection === 'objectives' && (
                <div className="space-y-4">
                  <div>
                    <Label>Employé *</Label>
                    <Select value={objectiveForm.employee_id} onValueChange={(val) => setObjectiveForm({...objectiveForm, employee_id: val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un employé" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map(emp => (
                          <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Titre de l'Objectif *</Label>
                    <Input value={objectiveForm.title} onChange={(e) => setObjectiveForm({...objectiveForm, title: e.target.value})} />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={objectiveForm.description} onChange={(e) => setObjectiveForm({...objectiveForm, description: e.target.value})} rows={3} />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Valeur Cible</Label>
                      <Input type="number" value={objectiveForm.target_value} onChange={(e) => setObjectiveForm({...objectiveForm, target_value: parseFloat(e.target.value)})} />
                    </div>
                    <div>
                      <Label>Unité</Label>
                      <Input value={objectiveForm.unit} onChange={(e) => setObjectiveForm({...objectiveForm, unit: e.target.value})} placeholder="%, nombre" />
                    </div>
                    <div>
                      <Label>Date Limite</Label>
                      <Input type="date" value={objectiveForm.deadline} onChange={(e) => setObjectiveForm({...objectiveForm, deadline: e.target.value})} />
                    </div>
                  </div>
                  <Button onClick={handleCreateObjective} className="w-full bg-gradient-to-r from-purple-600 to-pink-600">
                    Créer l'Objectif
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>
      )}

      {/* Content */}
      <div className="space-y-4">
        {/* Tasks Section */}
        {activeSection === 'tasks' && (
          tasks.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <CheckCircle className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <p className="text-lg text-gray-500">Aucune tâche assignée</p>
              </CardContent>
            </Card>
          ) : (
            tasks.map(task => (
              <Card key={task.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
                        <Badge className={getPriorityBadge(task.priority).color}>
                          {getPriorityBadge(task.priority).label}
                        </Badge>
                        {getStatusBadge(task.status)}
                      </div>
                      {(user.role === 'admin' || user.role === 'sub_admin') && (
                        <div className="flex items-center gap-2 mb-2 text-sm text-gray-600">
                          <User className="h-4 w-4" />
                          <span>{task.employee_name}</span>
                        </div>
                      )}
                      <p className="text-sm text-gray-700 mb-2">{task.description}</p>
                      {task.deadline && (
                        <p className="text-xs text-gray-500">
                          Deadline: {format(parseISO(task.deadline), 'dd/MM/yyyy', { locale: fr })}
                        </p>
                      )}
                    </div>
                    {user.role === 'employee' && task.status === 'en_cours' && (
                      <Button size="sm" className="bg-green-600" onClick={() => handleUpdateTaskStatus(task.id, 'termine')}>
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Marquer Terminé
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )
        )}

        {/* Reviews Section */}
        {activeSection === 'reviews' && (
          reviews.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Star className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <p className="text-lg text-gray-500">Aucune évaluation</p>
              </CardContent>
            </Card>
          ) : (
            reviews.map(review => (
              <Card key={review.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{review.employee_name}</h3>
                        <Badge className="bg-purple-100 text-purple-800">
                          {review.review_period}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-purple-600">{review.overall_rating}</span>
                        <span className="text-gray-500">/5</span>
                        <div className="flex gap-1 ml-2">
                          {[1,2,3,4,5].map(i => (
                            <Star key={i} className={`h-5 w-5 ${i <= review.overall_rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3 text-sm">
                    {review.strengths && (
                      <div>
                        <p className="font-semibold text-gray-700">Points Forts:</p>
                        <p className="text-gray-600">{review.strengths}</p>
                      </div>
                    )}
                    {review.areas_for_improvement && (
                      <div>
                        <p className="font-semibold text-gray-700">Axes d'Amélioration:</p>
                        <p className="text-gray-600">{review.areas_for_improvement}</p>
                      </div>
                    )}
                    {review.comments && (
                      <div>
                        <p className="font-semibold text-gray-700">Commentaires:</p>
                        <p className="text-gray-600">{review.comments}</p>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-4">
                    Évaluateur: {review.reviewer_name} - {format(parseISO(review.created_at), 'dd/MM/yyyy', { locale: fr })}
                  </p>
                </CardContent>
              </Card>
            ))
          )
        )}

        {/* Objectives Section */}
        {activeSection === 'objectives' && (
          objectives.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Target className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <p className="text-lg text-gray-500">Aucun objectif</p>
              </CardContent>
            </Card>
          ) : (
            objectives.map(objective => {
              const progress = objective.target_value ? (objective.current_value / objective.target_value) * 100 : 0;
              return (
                <Card key={objective.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{objective.title}</h3>
                          {getStatusBadge(objective.status)}
                        </div>
                        {(user.role === 'admin' || user.role === 'sub_admin') && (
                          <p className="text-sm text-gray-600 mb-2">{objective.employee_name}</p>
                        )}
                        <p className="text-sm text-gray-700 mb-4">{objective.description}</p>
                        
                        {/* Progress Bar */}
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Progression</span>
                            <span className="font-semibold text-purple-600">
                              {objective.current_value} / {objective.target_value} {objective.unit}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div
                              className="bg-gradient-to-r from-purple-600 to-pink-600 h-3 rounded-full transition-all"
                              style={{ width: `${Math.min(progress, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        {objective.deadline && (
                          <p className="text-xs text-gray-500 mt-2">
                            Deadline: {format(parseISO(objective.deadline), 'dd/MM/yyyy', { locale: fr })}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Update Progress (Employee only) */}
                    {user.role === 'employee' && objective.status === 'en_cours' && (
                      <div className="flex gap-2 mt-4">
                        <Input
                          type="number"
                          placeholder="Nouvelle valeur"
                          className="w-32"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleUpdateObjective(objective.id, e.target.value);
                              e.target.value = '';
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          onClick={(e) => {
                            const input = e.target.closest('.flex').querySelector('input');
                            handleUpdateObjective(objective.id, input.value);
                            input.value = '';
                          }}
                        >
                          Mettre à jour
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )
        )}
      </div>
      )}
    </div>
  );
};

export default Performances;
